import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Alert,
  Autocomplete,
  Avatar,
  Box, 
  Button,
  CircularProgress,
  Container, 
  Grid,
  Paper, 
  Tab, 
  Tabs, 
  TextField,
  Typography,
} from '@mui/material';
import PhoneInput2 from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import axios from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';
import { timezones } from '../utils/timezones';
import { getCountryFromPhoneCode, getSmartDefaultCountry } from '../utils/countryMappings';
import ActivitySessions from '../components/ActivitySessions';

// Define components outside of Profile
const PersonalInfoContent = ({ 
  formData, 
  setFormData,
  phoneData, 
  setPhoneData,
  isPhoneVerified,
  verificationStep,
  verificationCode,
  setVerificationCode,
  resendTimer,
  requestStatus,
  handleChange,
  handleSendVerification,
  handleVerifyCode,
  handleResendCode,
  handleCancelVerification,
  verificationCodeInputRef,
  addressInputRef,
  user
}) => {
  // Add custom styles for phone input dropdown
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .react-tel-input .country-list .country:hover {
        background-color: var(--mui-palette-action-hover) !important;
        color: var(--mui-palette-text-primary) !important;
      }
      .react-tel-input .country-list .country.highlight {
        background-color: var(--mui-palette-action-selected) !important;
        color: var(--mui-palette-text-primary) !important;
      }
      .react-tel-input .country-list .country {
        background-color: var(--mui-palette-background-paper) !important;
        color: var(--mui-palette-text-primary) !important;
        border-bottom: 1px solid var(--mui-palette-divider) !important;
      }
      .react-tel-input .country-list .country .country-name {
        color: var(--mui-palette-text-primary) !important;
      }
      .react-tel-input .country-list .country .dial-code {
        color: var(--mui-palette-text-secondary) !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Google Maps autocomplete for address
  React.useEffect(() => {
    const SCRIPT_ID = 'google-maps-places-script';
    
    const loadGoogleMapsScript = () => {
      if (window.google && window.google.maps && window.google.maps.places) {
        initAutocomplete();
        return;
      }

      if (document.getElementById(SCRIPT_ID)) {
        const checkLoaded = setInterval(() => {
          if (window.google && window.google.maps && window.google.maps.places) {
            clearInterval(checkLoaded);
            initAutocomplete();
          }
        }, 100);
        return;
      }

      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => initAutocomplete();
      script.onerror = () => console.error('Google Maps script failed to load');
      document.head.appendChild(script);
    };

    const initAutocomplete = () => {
      if (!addressInputRef.current) return;
      
      try {
        const autocomplete = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          {
            types: ['address'],
            fields: ['address_components', 'formatted_address', 'geometry']
          }
        );
        
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (!place.geometry) return;
          
          const addressComponents = {};
          place.address_components.forEach(component => {
            const types = component.types;
            
            if (types.includes('country')) {
              addressComponents.country = component.long_name;
            }
            
            if (types.includes('administrative_area_level_1')) {
              addressComponents.state = component.long_name;
            }
            
            if (types.includes('locality') || 
                types.includes('administrative_area_level_2') || 
                types.includes('sublocality') ||
                types.includes('sublocality_level_1')) {
              addressComponents.city = component.long_name;
            }
          });
          
          setFormData(prev => ({
            ...prev,
            address: place.formatted_address,
            ...addressComponents
          }));
        });
        
      } catch (error) {
        console.error('Error initializing autocomplete:', error);
      }
    };
    
    loadGoogleMapsScript();
    
    return () => {
      if (window.google && window.google.maps && addressInputRef.current) {
        try {
          window.google.maps.event.clearInstanceListeners(addressInputRef.current);
        } catch (error) {
          console.warn('Failed to clear Google Maps listeners:', error);
        }
      }
    };
  }, [setFormData, addressInputRef]);

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="First Name"
            name="first_name"
            value={formData.first_name}
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Last Name"
            name="last_name"
            value={formData.last_name}
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Role"
            value={formData.role}
            InputProps={{
              readOnly: true,
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>Phone Number</Typography>
          <PhoneInput2
            country={phoneData.countryCode ? getCountryFromPhoneCode(phoneData.countryCode) : getSmartDefaultCountry(user)}
            value={phoneData.fullNumber}
            onChange={(value, country) => {
              // Clean the value by removing any existing country code prefix
              const cleanValue = value.startsWith('+') ? value : `+${value}`;
              const nationalPart = cleanValue.replace(`+${country.dialCode}`, '').trim();
              
              setPhoneData({
                fullNumber: cleanValue,
                countryCode: country.dialCode,
                nationalNumber: nationalPart
              });
            }}
            disabled={isPhoneVerified || verificationStep !== 'input'}
            enableSearch={true}
            searchPlaceholder="Search country or dial code..."
            searchNotFound="No country found"
            containerStyle={{
              width: '100%',
              marginBottom: '8px'
            }}
            inputStyle={{
              width: '100%',
              height: '56px',
              fontSize: '0.875rem',
              paddingLeft: '76px',
              paddingRight: '16px',
              backgroundColor: 'transparent',
              color: 'inherit',
              border: '1px solid rgba(255, 255, 255, 0.23)',
              borderRadius: '4px',
              fontFamily: '"Roboto","Helvetica","Arial",sans-serif',
              boxSizing: 'border-box'
            }}
            buttonStyle={{
              backgroundColor: 'transparent',
              border: 'none',
              borderRight: '1px solid rgba(255, 255, 255, 0.23)',
              padding: '16px 12px',
              width: '62px',
              minWidth: '62px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
            dropdownStyle={{
              backgroundColor: 'var(--mui-palette-background-paper)',
              color: 'var(--mui-palette-text-primary)',
              border: '1px solid var(--mui-palette-divider)',
              borderRadius: '4px',
              maxHeight: '200px',
              overflowY: 'auto'
            }}
            searchStyle={{
              backgroundColor: 'var(--mui-palette-background-default)',
              color: 'var(--mui-palette-text-primary)',
              border: '1px solid var(--mui-palette-divider)',
              borderRadius: '4px',
              padding: '8px 12px',
              margin: '8px',
              outline: 'none'
            }}
            highlightCountryOnFlag={false}
            countryCodeEditable={false}
            specialLabel=""
            autoFormat={true}
            disableCountryCode={false}
            disableDropdown={false}
            enableAreaCodes={false}
            enableTerritories={true}
            inputProps={{
              autoComplete: 'tel'
            }}
            preferredCountries={['us', 'il', 'gb', 'de', 'fr']}
            priority={{
              us: 0,
              il: 1,
              gb: 2,
              de: 3,
              fr: 4
            }}
          />
          
          {/* Success message */}
          {requestStatus.success && (
            <Typography variant="body2" color="success.main" sx={{ mt: 1, mb: 1, display: 'block', fontWeight: 'bold' }}>
              ✅ {requestStatus.success}
            </Typography>
          )}
          
          {isPhoneVerified || verificationStep === 'verified' ? (
            <Typography variant="caption" color="success.main" sx={{ mt: 1, display: 'block' }}>
              ✅ Phone number verified
            </Typography>
          ) : (
            <Box sx={{ mt: 2 }}>
              {verificationStep === 'input' ? (
                <Button 
                  variant="contained" 
                  onClick={handleSendVerification}
                  disabled={!phoneData.fullNumber || requestStatus.isLoading}
                  sx={{ minWidth: '200px' }}
                >
                  {requestStatus.isLoading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    'Send Verification Code'
                  )}
                </Button>
              ) : verificationStep === 'code_sent' || verificationStep === 'verifying' ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-end' }}>
                    <TextField
                      label="Enter 6-digit code"
                      value={verificationCode}
                      onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      inputRef={verificationCodeInputRef}
                      inputProps={{ maxLength: 6, style: { textAlign: 'center', fontSize: '1.2rem', letterSpacing: '0.2em' } }}
                      sx={{ width: '200px' }}
                      autoComplete="one-time-code"
                    />
                    <Button 
                      variant="contained" 
                      onClick={handleVerifyCode}
                      disabled={verificationCode.length !== 6 || verificationStep === 'verifying'}
                      sx={{ minWidth: '120px', height: '56px' }}
                    >
                      {verificationStep === 'verifying' ? (
                        <CircularProgress size={24} color="inherit" />
                      ) : (
                        'Verify Code'
                      )}
                    </Button>
                  </Box>
                  
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Button 
                      variant="text" 
                      onClick={handleResendCode}
                      disabled={resendTimer > 0 || requestStatus.isLoading}
                      size="small"
                    >
                      {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
                    </Button>
                    <Button 
                      variant="text" 
                      onClick={handleCancelVerification}
                      size="small"
                      color="secondary"
                    >
                      Edit Phone
                    </Button>
                  </Box>
                </Box>
              ) : null}
            </Box>
          )}
        </Grid>

        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>Address Information</Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            inputRef={addressInputRef}
            placeholder="Start typing an address..."
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Country"
            name="country"
            value={formData.country}
            InputProps={{
              readOnly: true,
            }}
            helperText="Auto-filled from address"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="State/Province"
            name="state"
            value={formData.state}
            InputProps={{
              readOnly: true,
            }}
            helperText="Auto-filled from address"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="City"
            name="city"
            value={formData.city}
            InputProps={{
              readOnly: true,
            }}
            helperText="Auto-filled from address"
          />
        </Grid>
      </Grid>
    </Box>
  );
};
/*
const AddressContent = ({ 
  formData, 
  setFormData,
  addressInputRef,
  handleChange 
}) => {
  useEffect(() => {
    const SCRIPT_ID = 'google-maps-places-script';
    
    const loadGoogleMapsScript = () => {
      // Check if script is already loaded
      if (window.google && window.google.maps && window.google.maps.places) {
        initAutocomplete();
        return;
      }

      // Check if script is already being loaded
      if (document.getElementById(SCRIPT_ID)) {
        // Script is loading, wait for it
        const checkLoaded = setInterval(() => {
          if (window.google && window.google.maps && window.google.maps.places) {
            clearInterval(checkLoaded);
            initAutocomplete();
          }
        }, 100);
        return;
      }

      // Create and load script
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = () => initAutocomplete();
      script.onerror = () => console.error('Google Maps script failed to load');
      document.head.appendChild(script);
    };

    const initAutocomplete = () => {
      if (!addressInputRef.current) return;
      
      try {
        const autocomplete = new window.google.maps.places.Autocomplete(
          addressInputRef.current,
          {
            types: ['address'],
            fields: ['address_components', 'formatted_address', 'geometry']
          }
        );
        
        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace();
          if (!place.geometry) return;
          
          const addressComponents = {};
          place.address_components.forEach(component => {
            const types = component.types;
            
            // Country
            if (types.includes('country')) {
              addressComponents.country = component.long_name;
            }
            
            // State/Province/Region
            if (types.includes('administrative_area_level_1')) {
              addressComponents.state = component.long_name;
            }
            
            // City - check multiple possible types
            if (types.includes('locality') || 
                types.includes('administrative_area_level_2') || 
                types.includes('sublocality') ||
                types.includes('sublocality_level_1')) {
              addressComponents.city = component.long_name;
            }
          });
          
          setFormData(prev => ({
            ...prev,
            address: place.formatted_address,
            ...addressComponents
          }));
        });
        
      } catch (error) {
        console.error('Error initializing autocomplete:', error);
      }
    };
    
    loadGoogleMapsScript();
    
    // Cleanup when component unmounts
    return () => {
      const currentInput = addressInputRef.current;
      if (window.google && window.google.maps && currentInput) {
        try {
          window.google.maps.event.clearInstanceListeners(currentInput);
        } catch (error) {
          console.warn('Failed to clear Google Maps listeners:', error);
        }
      }
    };
  }, [setFormData, addressInputRef]);

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Address"
            name="address"
            value={formData.address}
            onChange={handleChange}
            inputRef={addressInputRef}
            placeholder="Start typing an address..."
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Country"
            name="country"
            value={formData.country}
            InputProps={{
              readOnly: true,
            }}
            helperText="Auto-filled from address"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="State/Province"
            name="state"
            value={formData.state}
            InputProps={{
              readOnly: true,
            }}
            helperText="Auto-filled from address"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="City"
            name="city"
            value={formData.city}
            InputProps={{
              readOnly: true,
            }}
            helperText="Auto-filled from address"
          />
        </Grid>
      </Grid>
    </Box>
  );
};
*/
const PreferencesContent = ({ 
  formData, 
  setFormData 
}) => {
  const timeFormatOptions = [
    { value: '12', label: '12-hour (12:30 PM)' },
    { value: '24', label: '24-hour (12:30)' }
  ];

  const dateFormatOptions = [
    { value: 'MM/DD/YYYY', label: 'MM/DD/YYYY (12/31/2024)' },
    { value: 'DD/MM/YYYY', label: 'DD/MM/YYYY (31/12/2024)' },
    { value: 'YYYY-MM-DD', label: 'YYYY-MM-DD (2024-12-31)' },
    { value: 'DD-MM-YYYY', label: 'DD-MM-YYYY (31-12-2024)' },
    { value: 'MM-DD-YYYY', label: 'MM-DD-YYYY (12-31-2024)' },
    { value: 'DD MMM YYYY', label: 'DD MMM YYYY (31 Dec 2024)' },
    { value: 'MMM DD, YYYY', label: 'MMM DD, YYYY (Dec 31, 2024)' }
  ];

  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={timezones}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Timezone"
                fullWidth
              />
            )}
            value={timezones.find(tz => tz.value === formData.timezone) || null}
            onChange={(event, newValue) => {
              setFormData(prev => ({
                ...prev,
                timezone: newValue?.value || ''
              }));
            }}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Autocomplete
            options={[{ value: 'en', label: 'English' }]}
            getOptionLabel={(option) => option.label}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Language"
                fullWidth
              />
            )}
            value={{ value: 'en', label: 'English' }}
            disabled
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Autocomplete
            options={timeFormatOptions}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Time Format"
                fullWidth
              />
            )}
            value={timeFormatOptions.find(tf => tf.value === formData.time_format) || timeFormatOptions[0]}
            onChange={(event, newValue) => {
              setFormData(prev => ({
                ...prev,
                time_format: newValue?.value || '12'
              }));
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Autocomplete
            options={dateFormatOptions}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Date Format"
                fullWidth
              />
            )}
            value={dateFormatOptions.find(df => df.value === formData.date_format) || dateFormatOptions[0]}
            onChange={(event, newValue) => {
              setFormData(prev => ({
                ...prev,
                date_format: newValue?.value || 'MM/DD/YYYY'
              }));
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const ProfessionalDetailsContent = ({ 
  formData, 
  handleChange,
  user 
}) => {
  return (
    <Box sx={{ mt: 3 }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Company"
            name="company"
            value={formData.company}
            InputProps={{
              readOnly: true,
            }}
            helperText="Managed by administrator"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Team"
            name="team"
            value={formData.team}
            onChange={handleChange}
            placeholder="e.g., Data Analytics Team, Marketing Team"
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Job Title"
            name="job_title"
            value={formData.job_title}
            onChange={handleChange}
            placeholder="e.g., Senior Data Analyst, Marketing Manager"
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label="Department"
            name="department"
            value={formData.department}
            onChange={handleChange}
            placeholder="e.g., Engineering, Marketing, Sales"
          />
        </Grid>
      </Grid>
    </Box>
  );
};
const Profile = () => {
  const { user, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const addressInputRef = useRef(null);
  
  // Define tab mapping
  const tabParamMap = useMemo(() => ({
    'personal': 0,
    'professional': 1,
    'connected-devices': 2,
    'activity': 3
  }), []);
  
  const paramTabMap = {
    0: 'personal',
    1: 'professional',
    2: 'connected-devices',
    3: 'activity'
  };
  
  // Get current tab from URL params
  const searchParams = new URLSearchParams(location.search);
  const currentTabParam = searchParams.get('tab') || 'personal';
  const initialTab = tabParamMap[currentTabParam] || 0;
  
  const [activeTab, setActiveTab] = useState(initialTab);
  
  // Add states for save functionality
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [profileSaveStatus, setProfileSaveStatus] = useState({
    isLoading: false,
    error: '',
    timeoutError: false
  });
  
  // Separate state for phone verification
  const [phoneRequestStatus, setPhoneRequestStatus] = useState({
    isLoading: false,
    error: '',
    success: '',
    timeoutError: false
  });

  const [formData, setFormData] = useState({
    first_name: user?.first_name || user?.given_name || '',
    last_name: user?.last_name || user?.family_name || '',
    email: user?.email || '',
    company: user?.company || '',
    team: user?.team || '',
    role: user?.role || '',
    department: user?.department || '',
    job_title: user?.job_title || '',
    country: user?.country || '',
    state: user?.state || '',
    city: user?.city || '',
    address: user?.address || '',
    notifications: true
  });
  
  const [phoneData, setPhoneData] = useState(() => {
    const phone = user?.phone_number || '';
    const countryCode = user?.country_code || '';
    
    // If we have a phone number but no country code, try to extract it
    let nationalNumber = '';
    if (phone && countryCode) {
      nationalNumber = phone.startsWith('+') 
        ? phone.replace(`+${countryCode}`, '')
        : phone.replace(countryCode, '');
    }
    
    return {
      fullNumber: phone,
      countryCode: countryCode,
      nationalNumber: nationalNumber
    };
  });

  const [isPhoneVerified, setIsPhoneVerified] = useState(user?.is_phone_verified || false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationStep, setVerificationStep] = useState('input'); // 'input', 'code_sent', 'verifying', 'verified'
  const [resendTimer, setResendTimer] = useState(0);
  const [error, setError] = useState('');
  const verificationCodeInputRef = useRef(null);

  // Update phone data when user changes (after verification)
  React.useEffect(() => {
    if (user) {
      const phone = user?.phone_number || '';
      const countryCode = user?.country_code || '';
      
      let nationalNumber = '';
      if (phone && countryCode) {
        nationalNumber = phone.startsWith('+') 
          ? phone.replace(`+${countryCode}`, '')
          : phone.replace(countryCode, '');
      }
      
      setPhoneData({
        fullNumber: phone,
        countryCode: countryCode,
        nationalNumber: nationalNumber
      });
      
      // Update verification states if phone is verified
      setIsPhoneVerified(user.is_phone_verified || false);
      if (user.is_phone_verified && verificationStep !== 'verified') {
        setVerificationStep('verified');
      }
    }
  }, [user, verificationStep]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    const tabParam = paramTabMap[newValue];
    navigate(`/profile?tab=${tabParam}`);
  };
  
  // Sync with URL changes (for browser back/forward)
  useEffect(() => {
    const newTab = tabParamMap[currentTabParam] || 0;
    if (newTab !== activeTab) {
      setActiveTab(newTab);
    }
  }, [currentTabParam, activeTab, tabParamMap]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({...prev, [name]: value}));
  };

  // Timer effect for resend countdown
  React.useEffect(() => {
    let interval;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Auto-focus verification code input when step changes to code_sent
  React.useEffect(() => {
    if (verificationStep === 'code_sent' && verificationCodeInputRef.current) {
      setTimeout(() => {
        verificationCodeInputRef.current.focus();
      }, 100); // Small delay to ensure DOM is updated
    }
  }, [verificationStep]);

  const startResendTimer = () => {
    setResendTimer(60); // 60 seconds
  };

  const handleSendVerification = async () => {
    try {
      if (!phoneData.countryCode || !phoneData.nationalNumber) {
        setError('Please enter a valid phone number');
        return;
      }

      setPhoneRequestStatus(prev => ({ ...prev, isLoading: true, error: '' }));

      const requestData = {
        country_code: phoneData.countryCode.replace('+', ''),
        phone_number: phoneData.nationalNumber
      };

      // Add timeout to SMS request
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      await axios.post('/auth/verify-phone', requestData, { 
        signal: controller.signal 
      });
      
      clearTimeout(timeoutId);
      setVerificationStep('code_sent');
      startResendTimer();
      setError('');
      setPhoneRequestStatus(prev => ({ ...prev, isLoading: false }));
    } catch (error) {
      setPhoneRequestStatus(prev => ({ ...prev, isLoading: false }));
      
      if (error.response?.status === 429) {
        const detail = error.response.data.detail;
        setError(detail.message || `Rate limit reached. Please wait ${detail.minutes_left} minutes before requesting another verification code.`);
      } else {
        setError(error.response?.data?.message || error.response?.data?.detail || 'Failed to send verification code');
      }
    }
  };

  const handleVerifyCode = async () => {
    setVerificationStep('verifying');
    setPhoneRequestStatus(prev => ({ ...prev, isLoading: true, error: '' }));
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // Increased to 30 seconds
  
      await axios.post('/auth/confirm-phone', {
        country_code: phoneData.countryCode.replace('+', ''),
        phone_number: phoneData.nationalNumber,
        code: verificationCode
      }, { signal: controller.signal });
  
      clearTimeout(timeoutId);
      
      // Update states immediately
      setVerificationStep('verified');
      setPhoneRequestStatus(prev => ({ ...prev, isLoading: false }));
      setError('');
      
      // Show success message immediately
      setPhoneRequestStatus(prev => ({ ...prev, success: 'Phone number verified successfully!' }));
      
      // Refresh user data from server
      try {
        await refreshUser();
      } catch (refreshError) {
        console.error('Failed to refresh user:', refreshError);
      }
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setPhoneRequestStatus(prev => ({ ...prev, success: '' }));
      }, 3000);
    } catch (error) {
      setVerificationStep('code_sent');
      if (error.name === 'AbortError') {
        setPhoneRequestStatus({
          isLoading: false,
          error: 'Request timeout. Please try again.',
          timeoutError: true
        });
      } else {
        setPhoneRequestStatus({
          isLoading: false,
          error: error.response?.data?.detail || 'Invalid or expired verification code',
          timeoutError: false
        });
        setVerificationCode(''); // Clear the code input
      }
    }
  };

  const handleCancelVerification = () => {
    setVerificationStep('input');
    setVerificationCode('');
    setResendTimer(0);
    setError('');
  };

  const handleResendCode = async () => {
    setVerificationCode('');
    await handleSendVerification();
  };

  // Add the save function
  const handleSaveProfile = async () => {
    try {
      setProfileSaveStatus({ isLoading: true, error: '', timeoutError: false });

      const profileData = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        team: formData.team,
        job_title: formData.job_title,
        department: formData.department,
        country: formData.country,
        state: formData.state,
        city: formData.city,
        address: formData.address
      };
      const response = await axios.put('/api/users/profile', profileData);
      if (response.status === 200) {
        setSaveSuccess(true);
        setTimeout(() => setSaveSuccess(false), 3000);
        setProfileSaveStatus({ isLoading: false, error: '', timeoutError: false });
        await refreshUser();
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setProfileSaveStatus({ isLoading: false, error: '', timeoutError: true });
      } else {
        setProfileSaveStatus({
          isLoading: false,
          error: error.response?.data?.detail || 'Failed to update profile',
          timeoutError: false
        });
      }
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Avatar 
            sx={{ width: 100, height: 100, mr: 3 }}
            src={user?.picture || ''}
            alt={`${user?.first_name} ${user?.last_name}`}
          />
          <Box>
            <Typography variant="h4">Your Profile</Typography>
            <Typography color="textSecondary">{user?.email}</Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {saveSuccess && (
          <Alert severity="success" sx={{ mb: 3 }}>
            Profile updated successfully!
          </Alert>
        )}

        <Tabs 
          value={activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="Personal Information" />
          <Tab label="Professional Details" />
          <Tab label="Connected Device" />
          <Tab label="Activity & Sessions" />
        </Tabs>

        {activeTab === 0 && (
          <PersonalInfoContent 
            formData={formData}
            setFormData={setFormData}
            phoneData={phoneData}
            setPhoneData={setPhoneData}
            isPhoneVerified={isPhoneVerified}
            verificationStep={verificationStep}
            verificationCode={verificationCode}
            setVerificationCode={setVerificationCode}
            resendTimer={resendTimer}
            requestStatus={phoneRequestStatus}
            handleChange={handleChange}
            handleSendVerification={handleSendVerification}
            handleVerifyCode={handleVerifyCode}
            handleResendCode={handleResendCode}
            handleCancelVerification={handleCancelVerification}
            verificationCodeInputRef={verificationCodeInputRef}
            addressInputRef={addressInputRef}
            user={user}
          />
        )}
        
        {activeTab === 1 && (
          <ProfessionalDetailsContent 
            formData={formData}
            handleChange={handleChange}
            user={user}
          />
        )}
        
        {activeTab === 2 && (
          <Box sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Connected Device
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Manage third-party applications and services connected to your account.
            </Typography>
            <Alert severity="info">
              Connected device management feature coming soon. You'll be able to view and manage 
              OAuth connections, API integrations, and third-party app permissions here.
            </Alert>
          </Box>
        )}
        
        {activeTab === 3 && (
          <ActivitySessions />
        )}
        
        {activeTab === 4 && (
          <PreferencesContent 
            formData={formData}
            setFormData={setFormData}
          />
        )}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button 
            variant="contained"
            onClick={handleSaveProfile}
            disabled={profileSaveStatus.isLoading}
          >
            {profileSaveStatus.isLoading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              'Save Changes'
            )}
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default Profile;